import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Edit, Trash2, Mail, Calendar } from "lucide-react";
import ModernTable from "../../../components/ModernTable";
import ConfirmationDialog from "../../../components/DialogBox";
import useContact from "./useContact";
import FollowupModal from "./Components/FollowupModal"

interface Contact {
  _id: string;
  userId?: {
    name: string;
  };
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  platform?: {
    name: string;
  };
  interest?: {
    type: string;
  };
  services?: {
    type: string;
  };
  outputStatus?: {
    status: string;
  };
  linkedin?: string;
  createdAt?: string;
  status: "Active" | "InActive";
}

const ContactPage: React.FC = () => {
  const {
    contactData,
    loading,
    search,
    page,
    totalPage,
    showConfirmation,
    showFollowupModal,
    selectedContact,
    handleSearch,
    handlePageChange,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleAdd,
    handleEmail,
    handleFollowup,
    handleCloseFollowupModal,
  } = useContact();

  console.log("contactData",contactData);
  const columns: ColumnDef<Contact>[] = useMemo(
    () => [
      {
        accessorKey: 'userId.name',
        header: 'CREATOR NAME',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.userId?.name || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'CLIENT NAME',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.name || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'DESCRIPTION',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900 text-wrap ">
            {row.original.description || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: 'EMAIL',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.email || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'PHONE',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.phone || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'companyName',
        header: 'COMPANY',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.companyName || 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'STATUS',
        cell: ({ row }) => (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            row.original.status === 'Active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'ACTIONS',
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleEdit(row.original._id)}
              className="text-green-600 hover:text-green-800 transition-colors"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original._id)}
              className="text-red-600 hover:text-red-800 transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleEmail(row.original._id)}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              title="Email"
            >
              <Mail className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleFollowup(row.original)}
              className="text-green-600 hover:text-green-800 transition-colors"
              title="Schedule Follow-up"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete, handleEmail, handleFollowup]
  );

  return (
    <div className="p-6 gap-2 h-[85vh]">
        <div className="flex justify-end mr-10">
        {/* <button className="bg-blue-500 text-white px-4 py-2 rounded-md my-4">Follow Up</button> */}
        </div>
      <ModernTable
        data={contactData}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        currentPage={page}
        totalPages={totalPage}
        onAdd={handleAdd}
        title="Contact"
        showSearch={true}
        showPagination={true}
        showAddButton={true}
      />

      {showConfirmation && (
        <ConfirmationDialog
          message="Are you sure you want to delete this contact? This action cannot be undone."
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showFollowupModal && (
        <FollowupModal
          isOpen={showFollowupModal}
          onClose={handleCloseFollowupModal}
          contact={selectedContact}
        />
      )}
    </div>
  );
};

export default ContactPage;
